<template>
    <div
        class="RiskProfileCriteriaTableRow"
        :class="{
            'RiskProfileCriteriaTableRow--warning': hasWarningState,
            'RiskProfileCriteriaTableRow--invalid': hasErrorState,
        }"
    >
        <div class="RiskProfileCriteriaTableRow__verify">
            <SwitchToggle
                v-tooltip="{ content: 'Verify' }"
                :value="criterion.verify"
                :disabled="shouldDisableVerify || !canManageCriteria"
                hide-label
                @input="updateVerification"
            />
        </div>

        <RiskProfileCriteriaTableRowTitle
            :is-verify="criterion.verify"
            :display-reference="displayReference"
            :custom-properties="customProperties"
            :display-metadata="displayMetadata"
            :has-advanced-settings="hasAdvancedSettings"
            :is-visible-value-dependent-field-placeholder="isVisibleValueDependentFieldPlaceholder"
        />

        <div class="RiskProfileCriteriaTableRow__actions">
            <div class="RiskProfileCriteriaTableRow__actionsContainer">
                <div v-if="hasErrorState || hasWarningState" class="flex items-center gap-2">
                    <EidIcon
                        v-if="hasAdvancedSettings && hasErrorState"
                        class="text-eidDangerRed"
                        alt="invalid configuration"
                        :icon-src="faExclamationTriangle"
                    />
                    <EidIcon
                        v-if="hasWarningState"
                        :class="'text-eidWarningYellow'"
                        :alt="warningMessage"
                        :icon-src="faExclamationTriangle"
                    />
                </div>
                <div class="RiskProfileCriteriaTableRow__action relative">
                    <template v-if="hasAdvancedSettings && canOpenSettings">
                        <NewButton
                            variant="flat"
                            size="icon-sm"
                            color="neutral"
                            :disabled="!criterion.verify"
                            aria-label="settings"
                            @click="openSettings"
                        >
                            <FontAwesomeIcon :icon="faCog" />
                        </NewButton>
                    </template>
                    <div
                        v-if="hasAdvancedSettings && !isRowValid"
                        class="bg-white absolute right-0 top-0 w-2 h-2 rounded-full flex items-center justify-center"
                    >
                        <div class="bg-eidDangerRed w-1.5 h-1.5 rounded-full" />
                    </div>
                </div>
                <div class="RiskProfileCriteriaTableRow__action">
                    <NewButton
                        v-if="deletable && canManageCriteria"
                        variant="flat"
                        size="icon-sm"
                        color="neutral"
                        aria-label="delete"
                        @click="deleteRow"
                    >
                        <EidIcon :icon-src="faXmark" />
                    </NewButton>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    @import "./index";
</style>

<script lang="ts">
    import { PropType } from 'vue';

    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { faXmark } from '@fortawesome/pro-light-svg-icons';
    import { faCog, faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { Checkbox } from '@evidentid/dashboard-commons/components/Checkbox';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { FormInput } from '@evidentid/dashboard-commons/components/Form';
    import { JsonSchemaFormElement } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import { SwitchToggle } from '@evidentid/dashboard-commons/components/SwitchToggle';
    import createForm from '@evidentid/json-schema/createForm';
    import JsonSchema from '@evidentid/json-schema/interfaces/JsonSchema';
    import { TprmSchema } from '@evidentid/tprm-portal-lib/models/common/TprmSchema.model';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import {
        CriterionDisplayMeta,
        CriterionTemplate,
    } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
    import { toTprmRequirementType } from '@evidentid/tprm-portal-lib/utils/common/toTprmRequirementType';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { IsReferenceStartsWithIsInvalid } from '@/modules/decisioning-criteria/components/CreateEditRiskProfile/types';
    import {
        CriteriaCanCustomPropertySubstitution,
    } from '@/modules/decisioning-criteria/components/RiskProfileCriteriaTable/types';
    import {
        EditableRequirementCriterion,
    } from '@/modules/decisioning-criteria/models/EditableRequirements.model';
    import {
        GeneralAggregateLimitReferences,
        requirementTypeFieldReferenceDelimiter,
    } from '@/modules/decisioning-criteria/types';
    import {
        getFlagDisabledReferenceValueSchema,
    } from '@/modules/decisioning-criteria/utils/getFlagDisabledReferenceValueSchema';
    import { addEnumLabelToSchemaIfAvailable } from '@/modules/decisioning-criteria/utils/riskProfileCriteria';
    import { standardizeCriterionSchema } from '@/modules/decisioning-criteria/utils/standardizeCriterionSchema';

    import RiskProfileCriteriaTableRowTitle from './sub-components/RiskProfileCriteriaTableRowTitle.vue';

    enum CriterionFieldString {
        generalAggregateLimit = 'coverage.generalAggregateLimit',
    }

    @Component({
        emits: [ 'delete', 'input', 'openSettings', 'validityChange' ],
        components: {
            Checkbox,
            EidIcon,
            FontAwesomeIcon,
            FormInput,
            JsonSchemaFormElement,
            NewButton,
            RiskProfileCriteriaTableRowTitle,
            SwitchToggle,
        },
    })
    export default class RiskProfileCriteriaTableRow extends Vue {
        @Prop({ type: Object, required: true })
        private criterion!: EditableRequirementCriterion;

        @Prop({ type: Object, required: true })
        private template!: CriterionTemplate;

        @Prop({ type: Boolean, default: false })
        private onlyRow!: boolean;

        @Prop({ type: Boolean, default: false })
        private showError!: boolean;

        @Prop({ type: Boolean, default: false })
        private showValidationErrors!: boolean;

        @Prop({ type: Boolean as PropType<boolean>, default: false })
        private someCustomPropertySubstitution!: boolean;

        @Prop({
            type: Object as PropType<CriteriaCanCustomPropertySubstitution>,
            default: (): CriteriaCanCustomPropertySubstitution => ({}),
        })
        private criteriaCanCustomPropertySubstitution!: CriteriaCanCustomPropertySubstitution;

        @Prop({ type: Array, default: () => [] })
        private customProperties!: CustomProperty[];

        @Prop({ type: Function as PropType<IsReferenceStartsWithIsInvalid>, required: true })
        private isReferenceStartsWithIsInvalid!: IsReferenceStartsWithIsInvalid;

        @Prop({ type: Boolean, default: true })
        private canManageCriteria!: boolean;

        @Prop({ type: Boolean, default: true })
        private canOpenSettings!: boolean;

        private faCog = faCog;
        private faXmark = faXmark;
        private faExclamationTriangle = faExclamationTriangle;

        @Watch('displayReferenceSchema', { immediate: true })
        private schemaChange() {
            if (this.displayReferenceSchema && this.displayReferenceKey && this.form) {
                this.$emit('validityChange', {
                    isValid: this.isInputRowValid,
                    reference: this.getCombinedReference(this.displayReferenceKey),
                });
            }
        }

        @Watch('template', { immediate: true })
        private templateChange() {
            if (this.criterion.verify) {
                this.validateAllReferences();
            }
        }

        /**
         * Re-check the criterion whenever its configuration changes, not only when the template does.
         *
         * The save button is gated entirely on the profile's invalid-reference list, and the only things
         * that ever wrote to it for this criterion were this row mounting, the verify toggle, and the
         * per-keystroke events emitted while a field is being edited. A configuration that changed by
         * any other route -- advanced settings closing, or a conditional reference dropping the input
         * that belonged to the branch the operator switched away from -- kept the verdict it was given
         * at mount. An emptied limit stayed "valid" and the profile saved with no limit at all.
         */
        @Watch('criterion.references', { deep: true })
        private referencesChange() {
            if (this.criterion.verify) {
                this.validateAllReferences();
            }
        }

        /**
         * A form per reference, built once per template rather than per validation.
         *
         * `validateAllReferences` now runs on every keystroke, because the criterion object is
         * replaced on every input all the way up to CreateEditRiskProfile, which commits live. The
         * forms do not depend on the values being typed -- only on the template, the client's custom
         * properties, and the substitution schema -- so they are cached here and the watch is left
         * to fire as often as it needs to.
         */
        private get referenceForms(): Record<string, ReturnType<typeof createForm>> {
            return Object.entries(this.template.evaluator.references).reduce((forms, [ name, templateRef ]) => {
                if (templateRef.schema) {
                    forms[name] = createForm(standardizeCriterionSchema(
                        templateRef.schema,
                        this.customProperties,
                        this.flagDisabledReferenceValueSchema,
                    ));
                }
                return forms;
            }, {} as Record<string, ReturnType<typeof createForm>>);
        }

        private get flagDisabledReferenceValueSchema(): TprmSchema | null {
            return getFlagDisabledReferenceValueSchema({ template: this.template });
        }

        private get displayMetadata(): CriterionDisplayMeta {
            return this.template.displayMetadata;
        }

        private get displayReferenceKey(): string | null {
            const conditionalRefs = this.displayMetadata.conditionalValueReference;
            if (conditionalRefs) {
                const flagRef = conditionalRefs.flagValueReference;
                const flagValue = this.criterion.references[flagRef];
                return flagValue ? conditionalRefs.flagEnabledReference : conditionalRefs.flagDisabledReference;
            } else {
                return this.displayMetadata.requiredValueReference || null;
            }
        }

        private get displayReference(): { value: any, schema: any } | null {
            if (!this.displayReferenceKey) {
                return null;
            }
            return {
                value: this.criterion.references[this.displayReferenceKey],
                schema: this.template.evaluator.references[this.displayReferenceKey]?.schema ?? null,
            };
        }

        private get displayReferenceSchema(): JsonSchema | null {
            if (this.displayReference) {
                const enumLabelsMap = this.displayMetadata
                    .enumValues?.find((x) => x.reference === this.displayMetadata.requiredValueReference)?.values;
                return enumLabelsMap
                    ? addEnumLabelToSchemaIfAvailable(this.displayReference.schema, enumLabelsMap)
                    : this.displayReference.schema;
            } else {
                return null;
            }
        }

        private get substitutionFlagValue(): boolean {
            if (this.isSubstituteDeterminedByConditionalRef) {
                const key = this.displayMetadata.conditionalValueReference?.flagValueReference;
                return key ? this.criterion.references[key] : false;
            } else {
                return this.displayReference?.value?.isBasedOnInsuredField || false;
            }
        }

        private get isRowValid(): boolean {
            const hasReference = Object.keys(this.template.evaluator.references).length > 0;
            if (hasReference || this.isVisibleValueDependentFieldPlaceholder) {
                const nonInputCriterionReferenceStart = this.getCombinedReference();
                return !this.isReferenceStartsWithIsInvalid(nonInputCriterionReferenceStart);
            }

            return true;
        }

        private get hasErrorState(): boolean {
            return !this.isRowValid && this.showValidationErrors;
        }

        private get hasWarningState(): boolean {
            return this.template.deprecated;
        }

        private get warningMessage(): string {
            return 'Deprecated configuration';
        }

        private get isVisibleValueDependentFieldPlaceholder(): boolean {
            return this.isCriterionInputValueDependent &&
                this.isCriterionInputDependentHasValue &&
                this.isCommercialGlAggregateLimit;
        }

        private get isCommercialGlAggregateLimit(): boolean {
            return this.criterion.coverageType === toTprmRequirementType('COMMERCIAL_GENERAL_LIABILITY') &&
                this.criterion.field === CriterionFieldString.generalAggregateLimit;
        }

        private get isCriterionInputValueDependent(): boolean {
            return this.isCommercialGlAggregateLimit;
        }

        private get isCriterionInputDependentHasValue(): boolean {
            if (this.isCommercialGlAggregateLimit) {
                const value = this.criterion.references[GeneralAggregateLimitReferences.conditionSets];
                return Boolean(value && value.length > 0);
            }
            return false;
        }

        private get isSubstituteDeterminedByConditionalRef(): boolean {
            return Boolean(this.template.displayMetadata.conditionalValueReference);
        }

        private get shouldDisableVerify(): boolean {
            return this.template.displayMetadata.verifyToggleDisabled || false;
        }

        private get deletable(): boolean {
            return !this.onlyRow && !this.template.displayMetadata.mandatory;
        }

        private updateVerification(value: boolean): void {
            this.$emit('input', { ...this.criterion, verify: value });
            if (value) {
                this.validateAllReferences();
            } else {
                this.$emit('validityChange', {
                    reference: this.getCombinedReference(),
                    isValid: true,
                });
            }
        }

        private openSettings(): void {
            if (this.criterion.verify) {
                this.$emit('openSettings', this.criterion);
            }
        }

        private deleteRow(): void {
            this.$emit('delete');
            this.$emit('validityChange', {
                isValid: true,
                reference: this.getCombinedReference(),
            });
        }

        private getCombinedReference(reference?: string): string {
            // no reference will emit only coverage type and field, will remove all criterion invalid references
            return [ this.criterion.coverageType, this.criterion.field, reference ]
                .filter(Boolean)
                .join(requirementTypeFieldReferenceDelimiter);
        }

        private validateAllReferences(): void {
            const hiddenReference = this.substitutionFlagValue
                ? this.displayMetadata.conditionalValueReference?.flagDisabledReference
                : this.displayMetadata.conditionalValueReference?.flagEnabledReference;
            Object.entries(this.template.evaluator.references).forEach(([ name, templateRef ]) => {
                if (name !== hiddenReference && templateRef.schema) {
                    const form = this.referenceForms[name];
                    const value = form.getValue(this.criterion.references[name], true);
                    this.$emit('validityChange', {
                        isValid: form.isValid(value, true),
                        reference: [ this.criterion.coverageType, this.criterion.field, name ]
                            .join(requirementTypeFieldReferenceDelimiter),
                    });
                }
            });
        }

        private get hasAdvancedSettings(): boolean {
            return Object.keys(this.template.evaluator.references).length > 0;
        }
    }
</script>
